import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TimeControl } from "./TimeControl";
import type { RundownItem } from "../../common/types/domain";

export interface RundownItemRowProps {
  item: RundownItem;
  index: number;
  isEditing: boolean;
  isLive: boolean;
  isDragging: boolean;
  showParallel: boolean;
  editForm: {
    title: string;
    description: string;
    duration: string;
    plannedTime: string;
    isParallel: boolean;
  };
  onEditFormChange: (form: {
    title: string;
    description: string;
    duration: string;
    plannedTime: string;
    isParallel: boolean;
  }) => void;
  onStartEditing: (item: RundownItem) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: (id: string) => void;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (index: number) => void;
}

export const RundownItemRow: React.FC<RundownItemRowProps> = ({
  item,
  index,
  isEditing,
  isLive,
  isDragging,
  showParallel,
  editForm,
  onEditFormChange,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const { t } = useTranslation();
  const [hoveredDescriptionId, setHoveredDescriptionId] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div
      data-testid={`rundown-item-${item.id}`}
      draggable={!isEditing}
      onDragStart={() => onDragStart(index)}
      onDragOver={onDragOver}
      onDrop={() => onDrop(index)}
      style={{
        display: "grid",
        gridTemplateColumns: "50px 30px 1.2fr 1.5fr 100px 100px 140px",
        alignItems: "center",
        background: isDragging
          ? "var(--color-bg-mesh)"
          : isLive
            ? "rgba(56, 189, 248, 0.1)"
            : "var(--color-bg-base)",
        border: isDragging
          ? "1px dashed var(--color-text-muted)"
          : isLive
            ? "1px solid var(--color-data-event)"
            : "1px solid var(--color-panel-border)",
        opacity: isDragging ? 0.4 : 1,
        padding: "15px",
        paddingLeft: item.isParallel ? "30px" : "15px",
        borderRadius: "var(--border-radius-md)",
        gap: "10px",
        cursor: isEditing ? "default" : "grab",
        transition: "all 0.2s",
      }}
    >
      {/* Index + Live badge */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: "1.4rem", fontWeight: "900", color: "var(--color-data-event)" }}>
          {index + 1}
        </span>
        {isLive && (
          <span
            style={{
              fontSize: "0.6rem",
              color: "var(--color-data-event)",
              fontWeight: "bold",
              marginTop: "4px",
            }}
          >
            {t("controller.rundownEditor.onAir")}
          </span>
        )}
      </div>

      {/* Parallel marker */}
      <div
        style={{
          color: "var(--color-accent-primary)",
          fontSize: "1rem",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {showParallel && "║"}
      </div>

      {isEditing ? (
        <>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <input
              data-testid="rundown-item-edit-title"
              value={editForm.title}
              onChange={(e) =>
                onEditFormChange({ ...editForm, title: e.target.value.toUpperCase() })
              }
              style={{
                background: "var(--color-input-bg)",
                border: "1px solid var(--color-panel-border)",
                color: "var(--color-text-primary)",
                padding: "8px",
                borderRadius: "var(--border-radius-sm)",
                width: "100%",
                outline: "none",
                fontWeight: "bold",
              }}
            />
            <div
              style={{
                fontSize: "0.6rem",
                color: "var(--color-text-muted)",
                marginTop: "2px",
                fontFamily: "var(--font-mono)",
              }}
            >
              ID: {item.id}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <input
              data-testid="rundown-item-edit-description"
              value={editForm.description}
              onChange={(e) => onEditFormChange({ ...editForm, description: e.target.value })}
              placeholder={t("controller.rundownEditor.descriptionOptional")}
              style={{
                background: "var(--color-input-bg)",
                border: "1px solid var(--color-panel-border)",
                color: "var(--color-text-secondary)",
                padding: "8px",
                borderRadius: "var(--border-radius-sm)",
                width: "100%",
                outline: "none",
                fontSize: "0.8rem",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{
                fontSize: "0.6rem",
                color: "var(--color-accent-warning)",
                fontWeight: "bold",
              }}
            >
              {t("controller.rundownEditor.startTime")}
            </span>
            <TimeControl
              value={editForm.plannedTime}
              onChange={(val) => onEditFormChange({ ...editForm, plannedTime: val })}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{ fontSize: "0.6rem", color: "var(--color-text-muted)", fontWeight: "bold" }}
            >
              {t("controller.rundownEditor.duration")}
            </span>
            <TimeControl
              value={editForm.duration}
              onChange={(val) => onEditFormChange({ ...editForm, duration: val })}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{ fontSize: "0.6rem", color: "var(--color-text-muted)", fontWeight: "bold" }}
            >
              {t("controller.rundownEditor.mode")}
            </span>
            <button
              onClick={() => onEditFormChange({ ...editForm, isParallel: !editForm.isParallel })}
              style={{
                background: editForm.isParallel
                  ? "rgba(45, 111, 247, 0.1)"
                  : "var(--color-input-bg)",
                border: `1px solid ${editForm.isParallel ? "var(--color-accent-primary)" : "var(--color-panel-border)"}`,
                color: editForm.isParallel
                  ? "var(--color-accent-primary)"
                  : "var(--color-text-muted)",
                borderRadius: "var(--border-radius-sm)",
                padding: "4px 8px",
                cursor: "pointer",
                fontSize: "0.65rem",
                fontWeight: "bold",
                transition: "all 0.2s",
              }}
            >
              {editForm.isParallel
                ? t("controller.rundownEditor.parallel")
                : t("controller.rundownEditor.sequence")}
            </button>
          </div>
          <div style={{ display: "flex", gap: "5px" }}>
            <button
              data-testid="rundown-item-save-btn"
              onClick={onSaveEdit}
              style={{
                background: "var(--color-accent-primary)",
                color: "rgba(255, 255, 255, 0.8)",
                border: "none",
                padding: "5px 12px",
                borderRadius: "var(--border-radius-sm)",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              {t("controller.rundownEditor.save")}
            </button>
            <button
              data-testid="rundown-item-cancel-btn"
              onClick={onCancelEdit}
              style={{
                background: "var(--color-bg-mesh)",
                color: "var(--color-text-muted)",
                border: "1px solid var(--color-panel-border)",
                padding: "5px 12px",
                borderRadius: "var(--border-radius-sm)",
                cursor: "pointer",
                fontSize: "0.75rem",
              }}
            >
              {t("controller.rundownEditor.cancel")}
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "1.1rem",
                color: isLive ? "#fff" : "var(--color-text-primary)",
              }}
            >
              {item.title}
            </div>
            <div
              style={{
                fontSize: "0.65rem",
                color: "var(--color-text-muted)",
                fontFamily: "var(--font-mono)",
                marginTop: "2px",
              }}
            >
              ID: {item.id}
            </div>
          </div>
          <div
            style={{ position: "relative", minWidth: 0 }}
            onMouseEnter={() => setHoveredDescriptionId(item.id)}
            onMouseLeave={() => setHoveredDescriptionId(null)}
          >
            <div
              style={{
                fontSize: "0.9rem",
                color: "var(--color-text-secondary)",
                fontStyle: "italic",
                lineHeight: "1.2",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: "help",
              }}
            >
              {item.description || "-"}
            </div>
            {hoveredDescriptionId === item.id && item.description && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  zIndex: 9999,
                  background: "var(--color-bg-base)",
                  border: "1px solid var(--color-accent-danger)",
                  borderRadius: "var(--border-radius-md)",
                  padding: "16px",
                  color: "var(--color-text-primary)",
                  width: "max-content",
                  maxWidth: "500px",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.8)",
                  marginTop: "8px",
                  fontStyle: "normal",
                  fontSize: "0.9rem",
                  lineHeight: "1.5",
                  letterSpacing: "0.5px",
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    fontSize: "0.65rem",
                    color: "rgba(255, 255, 255, 0.8)",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  Description
                </div>
                {item.description}
              </div>
            )}
          </div>
          <div
            style={{
              color: "var(--color-accent-warning)",
              fontFamily: "var(--font-mono)",
              fontSize: "1.1rem",
              fontWeight: "bold",
            }}
          >
            {item.plannedTime || "--:--:--"}
          </div>
          <div
            style={{
              color: "var(--color-text-secondary)",
              fontFamily: "var(--font-mono)",
              fontSize: "1.1rem",
              fontWeight: "bold",
            }}
          >
            {item.duration}
          </div>
          <div style={{ display: "flex", gap: "5px", justifyContent: "flex-end" }}>
            <button
              data-testid={`rundown-item-edit-btn-${item.id}`}
              onClick={() => onStartEditing(item)}
              style={{
                background: "var(--color-bg-mesh)",
                color: "var(--color-text-secondary)",
                border: "1px solid var(--color-panel-border)",
                padding: "8px 15px",
                borderRadius: "var(--border-radius-sm)",
                cursor: "pointer",
                fontSize: "0.8rem",
                fontWeight: "bold",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-panel-border)";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--color-bg-mesh)";
                e.currentTarget.style.color = "var(--color-text-secondary)";
              }}
            >
              {t("controller.rundownEditor.edit")}
            </button>
            {confirmingDelete ? (
              <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                <button
                  data-testid={`rundown-item-delete-confirm-btn-${item.id}`}
                  onClick={() => {
                    onDelete(item.id);
                    setConfirmingDelete(false);
                  }}
                  style={{
                    background: "var(--color-accent-danger)",
                    border: "none",
                    color: "#fff",
                    padding: "6px 10px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s",
                  }}
                >
                  ¿Eliminar?
                </button>
                <button
                  data-testid={`rundown-item-delete-cancel-btn-${item.id}`}
                  onClick={() => setConfirmingDelete(false)}
                  style={{
                    background: "var(--color-bg-mesh)",
                    border: "1px solid var(--color-panel-border)",
                    color: "var(--color-text-muted)",
                    padding: "6px 8px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.7rem",
                    transition: "all 0.2s",
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                data-testid={`rundown-item-delete-btn-${item.id}`}
                onClick={() => setConfirmingDelete(true)}
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-panel-border)",
                  color: "var(--color-text-muted)",
                  padding: "8px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent-danger)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-text-muted)")}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
